import { supabase } from '../config/supabase';
import { AdminClothingItem, AdminItemEngagement, ClothingItem, CreateClothingItemInput } from '../types';
import { analyticsService } from './analytics';

/**
 * True when the error means the engagement backend simply isn't there yet
 * (migrations not applied in this environment): missing RPC (PostgREST
 * PGRST202 / Postgres 42883) or missing table (42P01). Callers degrade
 * gracefully instead of surfacing these as hard errors.
 */
const isEngagementBackendMissing = (error: { code?: string } | null): boolean =>
  error?.code === 'PGRST202' || error?.code === '42883' || error?.code === '42P01';

/** Ordering offered by the Browse toolbar. */
export type ItemSortOrder = 'newest' | 'price_asc';

const SORT_COLUMNS: Record<ItemSortOrder, { column: string; ascending: boolean }> = {
  newest: { column: 'created_at', ascending: false },
  price_asc: { column: 'price', ascending: true },
};

export const itemsService = {
  async getItems(filters?: {
    category?: string;
    orderBy?: ItemSortOrder;
    minPrice?: number;
    maxPrice?: number;
    status?: string;
    clubId?: string;
    clubIds?: string[];
    search?: string;
    limit?: number;
    offset?: number;
    createdAfter?: Date;
  }): Promise<{ data: ClothingItem[]; total: number }> {
    // Simple query with optional club join
    let query = supabase
      .from('clothing_items')
      .select('*, seller:profiles(*), club:clubs(*)', { count: 'exact' });

    // Apply common filters
    const sort = SORT_COLUMNS[filters?.orderBy ?? 'newest'];
    query = query
      .eq('status', filters?.status || 'available')
      .order(sort.column, { ascending: sort.ascending })
      // Stable tiebreaker: rows sharing a sort value (e.g. the same price)
      // would otherwise be ordered arbitrarily per request, so offset
      // pagination could repeat or skip them across pages.
      .order('id', { ascending: true });

    if (filters?.category) {
      query = query.eq('category', filters.category);
    }

    if (filters?.minPrice) {
      query = query.gte('price', filters.minPrice);
    }

    if (filters?.maxPrice) {
      query = query.lte('price', filters.maxPrice);
    }

    if (filters?.clubId) {
      query = query.eq('club_id', filters.clubId);
    }

    // Filter by multiple club IDs (for "Recent from your clubs")
    if (filters?.clubIds && filters.clubIds.length > 0) {
      query = query.in('club_id', filters.clubIds);
    }

    // Server-side search filtering
    if (filters?.search && filters.search.trim()) {
      const searchTerm = `%${filters.search.trim()}%`;
      query = query.or(
        `title.ilike.${searchTerm},description.ilike.${searchTerm},category.ilike.${searchTerm}`
      );
    }

    // Date filtering for new items
    if (filters?.createdAfter) {
      query = query.gte('created_at', filters.createdAfter.toISOString());
    }

    // Apply pagination
    const limit = filters?.limit || 20;
    const offset = filters?.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], total: count || 0 };
  },

  async getItemById(id: string): Promise<ClothingItem | null> {
    const { data, error } = await supabase
      .from('clothing_items')
      .select('*, seller:profiles(*), club:clubs(*)')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Distinct-viewer count for one of the caller's own listings.
   * Returns 0 for items the caller does not own.
   */
  async getItemViewCount(itemId: string): Promise<number> {
    const { data, error } = await supabase.rpc('get_item_view_count', { p_item_id: itemId });
    if (error) {
      console.warn('Failed to load item view count:', error.message);
      return 0;
    }
    return (data as number) ?? 0;
  },

  /**
   * Distinct-viewer counts for all of the caller's own listings, keyed by item id.
   */
  async getMyListingViewCounts(): Promise<Record<string, number>> {
    const { data, error } = await supabase.rpc('get_my_listing_view_counts');
    if (error) {
      console.warn('Failed to load listing view counts:', error.message);
      return {};
    }
    const counts: Record<string, number> = {};
    for (const row of (data as Array<{ item_id: string; view_count: number }>) ?? []) {
      counts[row.item_id] = Number(row.view_count) || 0;
    }
    return counts;
  },

  async getUserItems(userId: string): Promise<ClothingItem[]> {
    // Verkochte advertenties gaan offline en horen niet meer tussen
    // "Mijn Advertenties" op de profielpagina.
    const { data, error } = await supabase
      .from('clothing_items')
      .select('*, seller:profiles(*), club:clubs(*)')
      .eq('seller_id', userId)
      .neq('status', 'sold')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createItem(item: CreateClothingItemInput): Promise<ClothingItem> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) throw new Error('User not authenticated');

    // Direct INSERT with relations
    const { data, error } = await supabase
      .from('clothing_items')
      .insert({
        seller_id: user.id,
        title: item.title,
        description: item.description,
        price: item.price,
        size: item.size,
        category: item.category,
        condition: item.condition,
        sport_type: item.sport_type || null,
        image_urls: item.image_urls,
        club_id: item.club_id,
      })
      .select('*, seller:profiles(*), club:clubs(*)')
      .single();

    if (error) throw error;
    analyticsService.trackListingCreated(data.category);
    return data;
  },

  async updateItem(id: string, updates: Partial<CreateClothingItemInput>): Promise<ClothingItem> {
    const { data, error } = await supabase
      .from('clothing_items')
      .update(updates)
      .eq('id', id)
      .select('*, seller:profiles(*), club:clubs(*)')
      .single();

    if (error) throw error;
    return data;
  },

  async deleteItem(id: string): Promise<void> {
    // Check for active transactions (pending or paid)
    const { data: activeTransaction, error: txError } = await supabase
      .from('transactions')
      .select('id, status')
      .eq('item_id', id)
      .in('status', ['pending', 'paid'])
      .maybeSingle();

    if (txError) throw txError;

    if (activeTransaction) {
      throw new Error('Cannot delete item - transaction in progress');
    }

    const { error } = await supabase
      .from('clothing_items')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getAllItemsAdmin(filters?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: AdminClothingItem[]; total: number }> {
    let query = supabase
      .from('clothing_items')
      .select('*, seller:profiles(id, first_name, last_name, email), club:clubs(id, name)', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters?.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }
    if (filters?.search?.trim()) {
      query = query.ilike('title', `%${filters.search.trim()}%`);
    }
    const limit = filters?.limit ?? 50;
    const offset = filters?.offset ?? 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data as AdminClothingItem[], total: count ?? 0 };
  },

  /**
   * Get per-item chat and view engagement counts for admins (admin-only,
   * enforced by the RPC). Returns aggregated counts only — message content
   * and viewer identities are never exposed. Every requested item gets an
   * entry (zeros when there is no activity yet). Returns null when the
   * engagement backend isn't available in this environment yet (migrations
   * not applied), so the UI can degrade instead of erroring.
   */
  async getItemEngagementAdmin(itemIds: string[]): Promise<Record<string, AdminItemEngagement> | null> {
    if (itemIds.length === 0) return {};

    const { data, error } = await supabase.rpc('get_admin_item_engagement', {
      p_item_ids: itemIds,
    });

    if (error) {
      if (isEngagementBackendMissing(error)) return null;
      throw error;
    }

    const engagement: Record<string, AdminItemEngagement> = {};
    for (const row of data ?? []) {
      engagement[row.item_id] = {
        conversationCount: Number(row.conversation_count ?? 0),
        messageCount: Number(row.message_count ?? 0),
        lastActivityAt: row.last_activity_at ?? null,
        // Older function versions don't return view_count yet — treat as 0
        viewCount: Number(row.view_count ?? 0),
      };
    }
    return engagement;
  },

  /**
   * Record that the current user viewed an item. Deduplicated server-side
   * (max one count per user per item per day); the seller's own views are
   * ignored. Callers should treat this as fire-and-forget; a missing backend
   * (migration not applied yet) is silently ignored.
   */
  async recordItemView(itemId: string): Promise<void> {
    const { error } = await supabase.rpc('record_item_view', { p_item_id: itemId });
    if (error && !isEngagementBackendMissing(error)) throw error;
  },

  async updateItemStatus(id: string, status: 'available' | 'reserved' | 'sold'): Promise<void> {
    const { error } = await supabase
      .from('clothing_items')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get current user's listings (My Listings)
   */
  async getMyListings(): Promise<ClothingItem[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('clothing_items')
      .select('*, seller:profiles(*), club:clubs(*)')
      .eq('seller_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },
};
